// ── What kind of thing is this, for a screen that has to draw it ─────────────
//
// Three components need the same answer and none of them owns it: the picker
// decides which preview to build, the viewer decides between an <img>, a
// <video> and a download link, and a module's own list decides which icon to
// put on a row. Written once so a fourth caller does not invent a fourth way of
// asking, which is how `image/heic` ends up counting as a photograph in two
// places and as "some file" in the third.
//
// It answers from the MIME type and never from the extension: the extension is
// the user's, the type is what the platform stored after checking the bytes
// against it (§4.4).

/** The type without its parameters, lower-cased. Same rule as the backend. */
export function baseMime(mimeType) {
  return String(mimeType ?? '').split(';')[0].trim().toLowerCase()
}

/**
 * `photo` | `video` | `audio` | `pdf` | `file`
 *
 * Deliberately five and not "media or not": a screen that shows a photograph in
 * a grid, plays a clip in place, plays a voice note in a little bar and offers
 * everything else as a download is making four different decisions.
 */
export function mediaKind(mimeType) {
  const type = baseMime(mimeType)
  if (type === 'application/pdf') return 'pdf'
  if (type.startsWith('image/')) return 'photo'
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('audio/')) return 'audio'
  return 'file'
}

/** Can this be shown full-screen rather than downloaded? */
export function isViewable(mimeType) {
  const kind = mediaKind(mimeType)
  return kind === 'photo' || kind === 'video'
}

/**
 * What goes in a file input's `accept`.
 *
 * The wildcards and not the list of containers, on purpose: `accept` is a hint
 * to the operating system's picker about which files to grey out, and a phone
 * that writes a container we do not store is better refused by the platform
 * with a sentence than hidden by a picker with no explanation at all. The
 * allow-list lives in one place and it is the backend's.
 */
export const ACCEPT = {
  photo: 'image/*',
  video: 'video/*',
  media: 'image/*,video/*',
}
